import { useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";

import { Box, Button, Heading, HStack, Stack } from "@chakra-ui/react";
import { keepPreviousData } from "@tanstack/react-query";
import { createColumnHelper, PaginationState, SortingState } from "@tanstack/react-table";

import { PostalCodeWithAgencyType } from "@/codegen/openapi2";
import { Switch } from "@/codegen/ui/switch";
import { toaster } from "@/codegen/ui/toaster";
import { TableLayout } from "@/components/2/layouts/table";
import { LegalEntityName } from "@/components/2/legal-entity";
import { LegalEntityContext } from "@/components/2/legal-entity-context";
import { Modal } from "@/components/2/modal";
import { usePermerRequirements } from "@/hooks/useHasPermission";
import {
  useDeletePostalCodesBindingGeographiesPostalCodesBindingDelete,
  useGetPostalCodesWithBindingsGeographiesPostalCodesWithBindingsGet,
  useUpsertPostalCodesBindingGeographiesPostalCodesBindingPut,
} from "@/services/2/react-query/hooks";
import { sortWriter } from "@/services/utils";

import { AgencyCell, GeographicalAgencyTypeCell } from "./cells";
import { PostalCodesFilters } from "./postal-codes-filters";
import { PostalCodesFiltersType } from "./types";

const columnHelper = createColumnHelper<PostalCodeWithAgencyType>();

const getFiltersQueryParameters = (
  filters: PostalCodesFiltersType,
  legalEntityContext: string | null
) => {
  return {
    exclude_unbound_postal_codes: filters.excludeUnboundPostalCodes,
    legal_entity_uuids: filters.entities?.length
      ? filters.entities
      : legalEntityContext
        ? [legalEntityContext]
        : null,
    postal_codes: filters.postalCodes?.length ? filters.postalCodes : null,
    city_names: filters.cityNames?.length ? filters.cityNames : null,
    agency_uuids: filters.agencies?.length ? filters.agencies : null,
    geographical_agency_types: filters.geographicalAgencyTypes?.length
      ? filters.geographicalAgencyTypes
      : null,
    global_search: filters.globalSearch || undefined,
  };
};

const sortMapping = {
  legal_entity_uuid: "legal_entity",
  postal_code: "postal_code",
  city_name: "city_name",
  agency_uuid: "agency",
};
const DEFAULT_FILTERS = {
  excludeUnboundPostalCodes: true,
};

export const PostalCodes = () => {
  const navigate = useNavigate();
  const legalEntityContext = useContext(LegalEntityContext);
  const [postalCodesFilters, setRawPostalCodesFilters] =
    useState<PostalCodesFiltersType>(DEFAULT_FILTERS);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });
  const [sorting, setRawSorting] = useState<SortingState>([]);
  const [isOpenUniqueConstraintViolationModal, setIsOpenUniqueConstraintViolationModal] =
    useState(false);
  const permissions = usePermerRequirements();
  const canEditPostalCodes = permissions.hasDsitGeographyPostalCodesEdit();

  const setPostalCodesFilters: typeof setRawPostalCodesFilters = useCallback(updater => {
    setRawPostalCodesFilters(updater);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, []);

  const setSorting: typeof setRawSorting = updater => {
    setRawSorting(updater);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  // Auto reset filters when legal entity context changes
  useEffect(() => {
    // This "setState in useEffect" is intentional and harmless.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPostalCodesFilters(DEFAULT_FILTERS);
    // `legalEntityContext` is the reset trigger, not a value read by the effect.
    // oxlint-disable-next-line react/exhaustive-effect-dependencies
  }, [legalEntityContext, setPostalCodesFilters]);

  const { mutate: upsertMutate } = useUpsertPostalCodesBindingGeographiesPostalCodesBindingPut();
  const { mutate: deleteMutate } = useDeletePostalCodesBindingGeographiesPostalCodesBindingDelete();
  const queryParams = {
    page: pagination.pageIndex + 1,
    size: pagination.pageSize,
    ...getFiltersQueryParameters(postalCodesFilters, legalEntityContext),
    ...(sorting && sorting.length > 0 ? sortWriter(sorting[0], sortMapping) : {}),
  };
  const { data } = useGetPostalCodesWithBindingsGeographiesPostalCodesWithBindingsGet(
    {
      query: queryParams,
    },
    ["GeographiesPostalCodesWithBindingsGet", queryParams],
    { placeholderData: keepPreviousData }
  );

  const upsertBinding = (
    row: PostalCodeWithAgencyType,
    agencyUuid: string,
    coreAgency: boolean | null,
    closeCoreAgency: boolean | null
  ) => {
    upsertMutate(
      {
        body: {
          postal_code: row.postal_code,
          insee_code: row.insee_code,
          legal_entity_uuid: row.legal_entity_uuid,
          agency_uuid: agencyUuid,
          core_agency: coreAgency ?? false,
          close_core_agency: closeCoreAgency ?? false,
        },
      },
      {
        onSuccess: binding => {
          toaster.create({
            title: "Le changement a bien été enregistré.",
            type: "success",
          });
          row.core_agency = binding.core_agency;
          row.close_core_agency = binding.close_core_agency;
          row.agency_uuid = binding.agency_uuid;
        },
        onError: error => {
          // Check for unique constraint violation (409 status)
          if (
            error &&
            typeof error === "object" &&
            "detail" in error &&
            Array.isArray(error.detail) &&
            error.detail[0]?.type === "UNIQUE_CONSTRAINT_VIOLATION"
          ) {
            setIsOpenUniqueConstraintViolationModal(true);
            return;
          }

          toaster.create({
            title: "Une erreur s'est produite lors de la modification.",
            type: "error",
          });
        },
      }
    );
  };

  const deleteBinding = (row: PostalCodeWithAgencyType) => {
    deleteMutate(
      {
        body: {
          insee_code: row.insee_code,
          postal_code: row.postal_code,
          legal_entity_uuid: row.legal_entity_uuid,
        },
      },
      {
        onSuccess: () => {
          toaster.create({
            title: "Le changement a bien été enregistré.",
            type: "success",
          });
          row.core_agency = null;
          row.close_core_agency = null;
          row.agency_uuid = null;
        },
        onError: () => {
          toaster.create({
            title: "Une erreur s'est produite lors de la modification.",
            type: "error",
          });
        },
      }
    );
  };

  const columns = [
    columnHelper.accessor("legal_entity_uuid", {
      header: "Entité",
      cell: ({ cell }) => <LegalEntityName legalEntityUuid={cell.getValue()} />,
      size: 200,
    }),
    columnHelper.accessor("postal_code", { header: "Code postal", size: 200 }),
    columnHelper.accessor("city_name", { header: "Ville", size: 250 }),
    columnHelper.accessor("agency_uuid", {
      header: "Agence",
      cell: ({ row }) => (
        <AgencyCell
          row={row}
          handleUpsert={canEditPostalCodes ? upsertBinding : undefined}
          handleDelete={canEditPostalCodes ? deleteBinding : undefined}
        />
      ),
      size: 250,
    }),
    columnHelper.display({
      header: "Intervention",
      cell: ({ row }) => (
        <GeographicalAgencyTypeCell
          row={row}
          handleUpsert={canEditPostalCodes ? upsertBinding : undefined}
        />
      ),
      size: 150,
    }),
  ];

  const postalCodes = data?.resources || [];
  const rowCount = data?.pagination?.item_count || 0;

  return (
    <Box>
      <Box p={8} backgroundColor={"gray.100"}>
        <HStack justify="space-between" align="center">
          <Heading size="2xl">Code postaux</Heading>
          {canEditPostalCodes && (
            <Button colorScheme="blue" onClick={() => navigate("import")}>
              Importer un fichier
            </Button>
          )}
        </HStack>
      </Box>

      <Stack p={8} gap={4} direction="column">
        <PostalCodesFilters filters={postalCodesFilters} setFilters={setPostalCodesFilters} />

        <Modal
          isOpen={isOpenUniqueConstraintViolationModal}
          setIsOpen={setIsOpenUniqueConstraintViolationModal}
          title={"Erreur liée à l'affectation de la zone d'intervention"}
          content={
            <p>
              L'agence d'une autre entité légale prestataire est déjà définie en "coeur zone" ou
              "zone proche" pour ce même code postal.
              <br />
              L'agence que vous souhaitez modifier doit donc être définie comme hors zone. Sinon,
              merci d'effectuer les modifications sur l'autre agence.
            </p>
          }
          maxWidth="md"
        />
        <TableLayout
          data={postalCodes || []}
          columns={columns}
          withManualPagination
          withSorting
          setSorting={setSorting}
          sorting={sorting}
          rowCount={rowCount}
          pagination={pagination}
          setPagination={setPagination}
        />

        <Switch
          onChange={() => {
            setPostalCodesFilters(prev => ({
              ...prev,
              excludeUnboundPostalCodes: !prev.excludeUnboundPostalCodes,
            }));
          }}
          checked={postalCodesFilters.excludeUnboundPostalCodes}
        >
          Masquer les codes postaux vides
        </Switch>
      </Stack>
    </Box>
  );
};
